import type { Rarity } from './global'

export interface LetterDef {
  id: number
  letter: string
  rarity: Rarity
  unlockCost: number // Ink to buy the letter (grants Lv1)
  baseOutput: number // Ink/s at Lv1
  trait?: string
  heat: number   // ink freshness contribution per letter on craft (frequent letters refresh, scarce letters dry)
  weight: number // roller pressure contribution per letter on craft (heavy type presses harder)
}

// Ported from alphabet_forge_balance.md Section 2 (English-frequency order).
// unlockCost = cost to reach Lv1; levelCost(L) = unlockCost * 1.12^(L-1).
// output(L) = baseOutput * 1.10^(L-1).
export const LETTERS: LetterDef[] = [
  { id: 1,  letter: 'E', rarity: 'Common',   unlockCost: 10,     baseOutput: 0.254, trait: 'Frequent', heat: 1,  weight: -1 },
  { id: 2,  letter: 'T', rarity: 'Common',   unlockCost: 42.87,  baseOutput: 0.181, trait: 'Frequent', heat: 0,  weight: 1 },
  { id: 3,  letter: 'A', rarity: 'Common',   unlockCost: 100.45, baseOutput: 0.163, trait: 'Frequent', heat: 1,  weight: -1 },
  { id: 4,  letter: 'O', rarity: 'Common',   unlockCost: 147.03, baseOutput: 0.15,  heat: 1,  weight: -1 },
  { id: 5,  letter: 'I', rarity: 'Common',   unlockCost: 234.92, baseOutput: 0.139, heat: 1,  weight: -1 },
  { id: 6,  letter: 'N', rarity: 'Common',   unlockCost: 344.51, baseOutput: 0.135, heat: 0,  weight: 1 },
  { id: 7,  letter: 'S', rarity: 'Common',   unlockCost: 476.21, baseOutput: 0.127, trait: 'Flexible', heat: 0,  weight: 1 },
  { id: 8,  letter: 'H', rarity: 'Common',   unlockCost: 630.34, baseOutput: 0.122, heat: 0,  weight: 1 },
  { id: 9,  letter: 'R', rarity: 'Uncommon', unlockCost: 807.23, baseOutput: 0.12, heat: 0,  weight: 1 },
  { id: 10, letter: 'D', rarity: 'Uncommon', unlockCost: 1258.93, baseOutput: 0.085, heat: 0, weight: 1 },
  { id: 11, letter: 'L', rarity: 'Uncommon', unlockCost: 1537.89, baseOutput: 0.081, heat: 0, weight: 1 },
  { id: 12, letter: 'C', rarity: 'Uncommon', unlockCost: 1846.21, baseOutput: 0.056, heat: 0, weight: 1 },
  { id: 13, letter: 'U', rarity: 'Uncommon', unlockCost: 2184.14, baseOutput: 0.055, heat: 1, weight: -1 },
  { id: 14, letter: 'M', rarity: 'Uncommon', unlockCost: 2551.93, baseOutput: 0.048, trait: 'Heavy', heat: 0, weight: 2 },
  { id: 15, letter: 'W', rarity: 'Uncommon', unlockCost: 2949.79, baseOutput: 0.047, trait: 'Heavy', heat: 0, weight: 2 },
  { id: 16, letter: 'F', rarity: 'Uncommon', unlockCost: 3377.94, baseOutput: 0.045, heat: 0, weight: 1 },
  { id: 17, letter: 'G', rarity: 'Uncommon', unlockCost: 3836.57, baseOutput: 0.04,  heat: 0, weight: 1 },
  { id: 18, letter: 'Y', rarity: 'Rare',     unlockCost: 4325.86, baseOutput: 0.039, heat: 0, weight: 1 },
  { id: 19, letter: 'P', rarity: 'Rare',     unlockCost: 4845.99, baseOutput: 0.039, heat: 0, weight: 1 },
  { id: 20, letter: 'B', rarity: 'Rare',     unlockCost: 5397.13, baseOutput: 0.03,  heat: 0, weight: 1 },
  { id: 21, letter: 'V', rarity: 'Rare',     unlockCost: 5979.44, baseOutput: 0.02,  trait: 'Scarce', heat: -1, weight: 0 },
  { id: 22, letter: 'K', rarity: 'Legend',   unlockCost: 6593.07, baseOutput: 0.015, trait: 'Scarce', heat: -1, weight: 0 },
  { id: 23, letter: 'J', rarity: 'Legend',   unlockCost: 7238.16, baseOutput: 0.003, trait: 'Scarce', heat: -1, weight: 0 },
  { id: 24, letter: 'X', rarity: 'Legend',   unlockCost: 7914.87, baseOutput: 0.003, trait: 'Scarce', heat: -1, weight: 0 },
  { id: 25, letter: 'Q', rarity: 'Legend',   unlockCost: 8623.31, baseOutput: 0.002, trait: 'Scarce', heat: -1, weight: 0 },
  { id: 26, letter: 'Z', rarity: 'Legend',   unlockCost: 9363.63, baseOutput: 0.0014, trait: 'Scarce', heat: -1, weight: 0 },
]

export const LETTERS_BY_ID: Record<number, LetterDef> = Object.fromEntries(
  LETTERS.map((l) => [l.id, l]),
)

// Uppercase char -> letter id, for resolving the letters that make up a word.
export const LETTER_ID_BY_CHAR: Record<string, number> = Object.fromEntries(
  LETTERS.map((l) => [l.letter, l.id]),
)

// Fast lookups for forge metric deltas during word craft.
export const LETTER_HEAT_BY_CHAR: Record<string, number> = Object.fromEntries(
  LETTERS.map((l) => [l.letter, l.heat]),
)
export const LETTER_WEIGHT_BY_CHAR: Record<string, number> = Object.fromEntries(
  LETTERS.map((l) => [l.letter, l.weight]),
)
